import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Box, TextField, Button, Grid, Paper,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, InputAdornment, Card, CardContent, CardActions,
  CardMedia, ImageList, ImageListItem, ImageListItemBar, Fab,
  Divider, Collapse, alpha
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Cached as CachedIcon, 
  Add as AddIcon, 
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Place as PlaceIcon,
  Straighten as SizeIcon,
  Close as CloseIcon,
  PhotoLibrary as GalleryIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';

import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

// Enhanced TattooImageItem component with edit functionality
const TattooImageItem = ({ tattoo, isEditing, onDelete, onEdit, api, clientId }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadImage = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = await api.fetchTattooImage(clientId, tattoo.id);
        if (isMounted) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('Error loading tattoo image:', error);
        if (isMounted) {
          setError('Failed to load image');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImage();

    // Cleanup function to revoke blob URL
    return () => {
      isMounted = false;
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [tattoo.id, clientId]); // Removed api from dependencies - it's stable

  return (
    <ImageListItem>
      {loading ? (
        <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
          <Typography variant="caption" color="error">Failed to load</Typography>
        </Box>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={tattoo.description}
          loading="lazy"
          style={{ height: 150, objectFit: 'cover', width: '100%' }}
        />
      ) : (
        <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
          <Typography variant="caption">Image not available</Typography>
        </Box>
      )}
      <ImageListItemBar
        title={tattoo.description}
        subtitle={`${tattoo.tattoo_date ? new Date(tattoo.tattoo_date).toLocaleDateString() : ''} ${tattoo.body_location ? `- ${tattoo.body_location}` : ''}`}
        actionIcon={
          isEditing && (
            <Box sx={{ display: 'flex' }}>
              <IconButton
                sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                onClick={() => onEdit(tattoo)}
                title="Edit tattoo details"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                onClick={onDelete}
                title="Delete tattoo"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )
        }
      />
    </ImageListItem>
  );
};

const ClientProfilePage = () => {
  const { id } = useParams();  
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const tattooFileInputRef = useRef(null);
  const api = UseApi();
  const { 
    isStandardSubscriber, 
    isGrowthStudioSubscriber 
  } = UseAuthRoles();
  
  // Check if user has access to advanced features (Standard or Growth Studio only)
  // Note: Starter Studio gets basic client profile - they need Growth Studio for advanced features
  const hasAdvancedAccess = isStandardSubscriber || isGrowthStudioSubscriber;

  const [client, setClient] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    date_of_birth: '',
    emergency_contact: '',
    emergency_phone: '',
    preferred_styles: '',
    allergies: '',
    medical_conditions: '',
    referral_source: '',
    notes: '',
    client_id_image_url: null,
  });
  
  // States for secure image loading
  const [secureImageSrc, setSecureImageSrc] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);

  // Tattoo history states
  const [tattooHistory, setTattooHistory] = useState([]);
  const [tattooHistoryLoading, setTattooHistoryLoading] = useState(false);
  const [selectedTattooFile, setSelectedTattooFile] = useState(null);
  const [tattooUploadLoading, setTattooUploadLoading] = useState(false);
  const [newTattooDescription, setNewTattooDescription] = useState('');
  const [newTattooDate, setNewTattooDate] = useState('');
  const [newTattooLocation, setNewTattooLocation] = useState('');
  
  // Tattoo editing states
  const [editingTattoo, setEditingTattoo] = useState(null);
  const [editTattooDialogOpen, setEditTattooDialogOpen] = useState(false);
  const [editTattooDescription, setEditTattooDescription] = useState('');
  const [editTattooDate, setEditTattooDate] = useState('');
  const [editTattooLocation, setEditTattooLocation] = useState('');
  const [tattooUpdateLoading, setTattooUpdateLoading] = useState(false);
  
  // Style preferences management
  const [stylePreferences, setStylePreferences] = useState([]);
  const [newStyle, setNewStyle] = useState('');

  const [selectedFile, setSelectedFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);  
  const [error, setError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false);
  const [tattooDeleteDialogOpen, setTattooDeleteDialogOpen] = useState(false);
  const [tattooToDelete, setTattooToDelete] = useState(null);

  // Client inquiries states
  const [clientInquiries, setClientInquiries] = useState([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [inquiriesExpanded, setInquiriesExpanded] = useState(true);

  // Image lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Reference to track if we've already fetched the image
  const imageUrlRef = useRef(null);
  
  // Use a ref to track if the component is mounted
  const isMounted = useRef(true);
  
  // Track if initial fetch has been done
  const initialFetchDone = useRef(false);

  // Memoize fetchClient to prevent unnecessary recreations
  const fetchClient = useCallback(async () => {
    if (!id) {
      setError('Client ID is missing from the URL');
      setLoading(false);
      return;
    }

    try {
      const data = await api.fetchClient(id);
      
      if (!isMounted.current) return;
      
      setClient(data);
      setLoading(false);
    } catch (error) {
      if (!isMounted.current) return;
      
      console.error("Error fetching client:", error);
      setError('Error fetching client data. Please try again.');
      setLoading(false);
    }
  }, [id, api]);

  // Memoize fetchTattooHistory to prevent unnecessary recreations
  const fetchTattooHistory = useCallback(async () => {
    if (!id) return;
    setTattooHistoryLoading(true);
    try {
      const history = await api.fetchClientTattooHistory(id);
      if (isMounted.current) {
        setTattooHistory(history);
      }
    } catch (error) {
      console.error("Error fetching tattoo history:", error);
    } finally {
      if (isMounted.current) {
        setTattooHistoryLoading(false);
      }
    }
  }, [id, api]);

  // Fetch client inquiries
  const fetchClientInquiries = useCallback(async () => {
    if (!id) return;
    
    setInquiriesLoading(true);
    try {
      const response = await api.fetchWithAuth(`/clients/${id}/inquiries/`);
      if (isMounted.current) {
        setClientInquiries(response.inquiries || []);
      }
    } catch (error) {
      console.error("Error fetching client inquiries:", error);
    } finally {
      if (isMounted.current) {
        setInquiriesLoading(false);
      }
    }
  }, [id, api]);

  // Initial fetch of client data - runs only once when id changes
  useEffect(() => {
    if (initialFetchDone.current && id === initialFetchDone.current) {
      return; // Already fetched for this id
    }
    
    isMounted.current = true;
    initialFetchDone.current = id;
    
    fetchClient();
    fetchClientInquiries();
    if (hasAdvancedAccess) {
      fetchTattooHistory();
    }
    
    // Cleanup - mark component as unmounted when it's destroyed
    return () => {
      isMounted.current = false;
    };
  }, [id]); // Only depend on id - not on functions or subscription status
  
  // Separate effect for when subscription status changes (user upgrades/downgrades)
  useEffect(() => {
    // Only fetch tattoo history if we have advanced access and haven't fetched yet
    if (hasAdvancedAccess && tattooHistory.length === 0 && !tattooHistoryLoading && id && initialFetchDone.current === id) {
      fetchTattooHistory();
    }
  }, [hasAdvancedAccess]); // Only run when subscription status changes

  // Improved image loading effect with dependency tracking
  useEffect(() => {
    if (client?.id && 
        client.client_id_image_url && 
        client.client_id_image_url !== imageUrlRef.current) {
      
      imageUrlRef.current = client.client_id_image_url;
      
      const fetchSecureImage = async () => {
        if (!isMounted.current) return;
        
        setImageLoading(true);
        setImageError(null);
        
        try {
          const imageUrl = await api.fetchClientImage(client.id);
          
          if (!isMounted.current) return;
          
          setSecureImageSrc(imageUrl);
        } catch (error) {
          if (!isMounted.current) return;
          
          console.error("Error fetching secure image:", error);
          setImageError(error.message);
          setSecureImageSrc(client.client_id_image_url);
        } finally {
          if (isMounted.current) {
            setImageLoading(false);
          }
        }
      };
      
      fetchSecureImage();
    }
    
    return () => {
      if (secureImageSrc && secureImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(secureImageSrc);
      }
    };
  }, [client?.id, client?.client_id_image_url]); // Removed api from dependencies

  // Parse style preferences when client data changes
  useEffect(() => {
    if (client.preferred_styles) {
      const styles = client.preferred_styles.split(',').map(style => style.trim()).filter(style => style);
      setStylePreferences(styles);
    }
  }, [client.preferred_styles]);

  // Lightbox handlers
  const handleOpenLightbox = (images, startIndex = 0) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxIndex(0);
  };

  const handleLightboxPrev = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1));
  };

  const handleLightboxNext = () => {
    setLightboxIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0));
  };

  // Format functions for inquiry display
  const formatBudget = (budget) => {
    const budgets = {
      under_200: 'Under $200',
      '200_500': '$200 - $500',
      '500_1000': '$500 - $1,000',
      '1000_2500': '$1,000 - $2,500',
      '2500_plus': '$2,500+',
      discuss: 'To discuss'
    };
    return budgets[budget] || budget || 'Not specified';
  };

  const formatTimeframe = (timeframe) => {
    const timeframes = {
      asap: 'ASAP',
      month: 'Within a month',
      '3_months': 'Within 3 months',
      '6_months': 'Within 6 months',
      flexible: 'Flexible'
    };
    return timeframes[timeframe] || timeframe || 'Not specified';
  };

  const handleInputChange = (e) => {
    setClient({ ...client, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      // Combine style preferences back to string
      const clientData = {
        ...client,
        preferred_styles: stylePreferences.join(', ')
      };
      
      // Remove image-related fields to avoid conflicts during updates
      // Images are handled separately through dedicated upload endpoints
      const { client_id_image_url, client_id_image, ...cleanClientData } = clientData;
      const data = await api.updateClient(id, cleanClientData);
      setClient(data);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating client:", error);
      setError('Error updating client. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteClient(id);
      navigate('/clients'); 
    } catch (error) {
      console.error("Error deleting client:", error);
      setError('Error deleting client. Please try again.');
    }
  };

  const handleViewConsentForms = () => {
    if (!id) return;
    const safeId = encodeURIComponent(id); 
    navigate(`/client/${safeId}/consent-forms`);
  };

  // Style preferences management
  const handleAddStyle = () => {
    if (newStyle.trim() && !stylePreferences.includes(newStyle.trim())) {
      setStylePreferences([...stylePreferences, newStyle.trim()]);
      setNewStyle('');
    }
  };
  
  const handleRemoveStyle = (styleToRemove) => {
    setStylePreferences(stylePreferences.filter(style => style !== styleToRemove));
  };
  
  const handleStyleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddStyle();
    }
  };

  // File handling for ID image
  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert("Please select a file to upload.");
      return;
    }

    setUploadLoading(true);
    
    try {
      // This uses the dedicated image upload endpoint
      await api.uploadClientImage(id, selectedFile);
      await fetchClient();
      setSelectedFile(null);
      imageUrlRef.current = null;
      alert("Client ID uploaded successfully!");
    } catch (error) {
      console.error("Error uploading ID:", error);
      setError("Error uploading client ID. Please try again.");
    } finally {
      setUploadLoading(false);
    }
  };

  // Tattoo history file handling
  const handleTattooFileSelect = () => {
    tattooFileInputRef.current.click();
  };

  const handleTattooFileChange = (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    setSelectedTattooFile(file);
  };

  const handleTattooUpload = async () => {
    if (!selectedTattooFile || !newTattooDescription.trim()) {
      alert("Please select a file and provide a description.");
      return;
    }

    setTattooUploadLoading(true);
    
    try {
      const tattooData = {
        description: newTattooDescription,
        date: newTattooDate || new Date().toISOString().split('T')[0],
        location: newTattooLocation
      };
      
      // This uses the dedicated tattoo image upload endpoint
      await api.uploadClientTattooImage(id, selectedTattooFile, tattooData);
      
      // Reset form
      setSelectedTattooFile(null);
      setNewTattooDescription('');
      setNewTattooDate('');
      setNewTattooLocation('');
      
      // Refresh tattoo history
      await fetchTattooHistory();
      
      alert("Tattoo image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading tattoo image:", error);
      setError("Error uploading tattoo image. Please try again.");
    } finally {
      setTattooUploadLoading(false);
    }
  };

  const handleDeleteTattoo = async () => {
    if (!tattooToDelete) return;
    
    try {
      await api.deleteClientTattooImage(id, tattooToDelete.id);
      await fetchTattooHistory();
      setTattooDeleteDialogOpen(false);
      setTattooToDelete(null);
    } catch (error) {
      console.error("Error deleting tattoo image:", error);
      setError("Error deleting tattoo image. Please try again.");
    }
  };

  // New tattoo edit handlers
  const handleEditTattoo = (tattoo) => {
    setEditingTattoo(tattoo);
    setEditTattooDescription(tattoo.description || '');
    setEditTattooDate(tattoo.tattoo_date || '');
    setEditTattooLocation(tattoo.body_location || '');
    setEditTattooDialogOpen(true);
  };

  const handleUpdateTattoo = async () => {
    if (!editingTattoo || !editTattooDescription.trim()) {
      alert("Please provide a description.");
      return;
    }

    setTattooUpdateLoading(true);
    
    try {
      const tattooData = {
        description: editTattooDescription,
        tattoo_date: editTattooDate || null,
        body_location: editTattooLocation || null
      };
      
      await api.updateClientTattooImage(id, editingTattoo.id, tattooData);
      
      // Refresh tattoo history
      await fetchTattooHistory();
      
      // Close dialog and reset form
      setEditTattooDialogOpen(false);
      setEditingTattoo(null);
      setEditTattooDescription('');
      setEditTattooDate('');
      setEditTattooLocation('');
      
    } catch (error) {
      console.error("Error updating tattoo:", error);
      setError("Error updating tattoo. Please try again.");
    } finally {
      setTattooUpdateLoading(false);
    }
  };
  
  // Functions for image replacement and removal
  const handleReplaceImage = () => {
    handleFileSelect();
  };
  
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };
  
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const openDeleteImageDialog = () => {
    setDeleteImageDialogOpen(true);
  };

  const closeDeleteImageDialog = () => {
    setDeleteImageDialogOpen(false);
  };
  
  const handleRemoveImage = async () => {
    setDeleteImageDialogOpen(false);
    
    try {
      setImageLoading(true);
      // This uses the dedicated image removal endpoint
      await api.removeClientImage(id);
      await fetchClient();
      setSecureImageSrc(null);
      imageUrlRef.current = null;
    } catch (error) {
      console.error("Error removing ID:", error);
      setError("Error removing client ID. Please try again.");
    } finally {
      setImageLoading(false);
    }
  };

  if (loading) {
    return <Typography>Loading client data...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ padding: 3, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isEditing ? (
            <>
              <TextField
                label="First Name"
                name="first_name"
                value={client.first_name || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Last Name"
                name="last_name"
                value={client.last_name || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
            </>
          ) : (
            `${client.first_name} ${client.last_name}`
          )}
        </Typography>

        <Grid container spacing={3}>
          {/* Left Column - Basic and Additional Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={client.email || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">Email: {client.email || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    value={client.phone_number || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">Phone: {client.phone_number || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={client.address || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">Address: {client.address || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={client.city || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">City: {client.city || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={client.state || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">State: {client.state || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="zip_code"
                    value={client.zip_code || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">ZIP: {client.zip_code || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    name="date_of_birth"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={client.date_of_birth || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">
                    Date of Birth: {client.date_of_birth ? new Date(client.date_of_birth).toLocaleDateString() : 'N/A'}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Emergency Contact"
                    name="emergency_contact"
                    value={client.emergency_contact || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">Emergency Contact: {client.emergency_contact || 'N/A'}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Emergency Phone"
                    name="emergency_phone"
                    value={client.emergency_phone || ''}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1">Emergency Phone: {client.emergency_phone || 'N/A'}</Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Basic Notes */}
            <Typography variant="h6" gutterBottom>Notes</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={client.notes || ''}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={4}
              />
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{client.notes || 'N/A'}</Typography>
            )}
          </Grid>

          {/* Right Column - Advanced Profile Features */}
          <Grid item xs={12} md={6}>
            {/* Advanced Profile Section for Standard and higher tiers */}
            {hasAdvancedAccess ? (
              <>
                <Typography variant="h6" gutterBottom>Advanced Profile</Typography>
                
                {/* Style Preferences */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Tattoo Style Preferences
                  </Typography>
                  
                  {isEditing && (
                    <TextField
                      fullWidth
                      label="Add Style Preference"
                      value={newStyle}
                      onChange={(e) => setNewStyle(e.target.value)}
                      onKeyPress={handleStyleInputKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleAddStyle}
                              disabled={!newStyle.trim()}
                              edge="end"
                            >
                              <AddIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                  )}
                  
                  {/* Display style preferences */}
                  {stylePreferences.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {stylePreferences.map((style, index) => (
                        <Chip
                          key={index}
                          label={style}
                          onDelete={isEditing ? () => handleRemoveStyle(style) : undefined}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No style preferences added
                    </Typography>
                  )}
                </Box>

                {/* Health Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Health Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="Allergies"
                          name="allergies"
                          value={client.allergies || ''}
                          onChange={handleInputChange}
                          multiline
                          rows={2}
                          margin="normal"
                        />
                      ) : (
                        <Typography variant="body2">
                          <strong>Allergies:</strong> {client.allergies || 'None reported'}
                        </Typography>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="Medical Conditions"
                          name="medical_conditions"
                          value={client.medical_conditions || ''}
                          onChange={handleInputChange}
                          multiline
                          rows={2}
                          margin="normal"
                        />
                      ) : (
                        <Typography variant="body2">
                          <strong>Medical Conditions:</strong> {client.medical_conditions || 'None reported'}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                {/* Referral Source */}
                <Box sx={{ mb: 3 }}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Referral Source"
                      name="referral_source"
                      value={client.referral_source || ''}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  ) : (
                    <Typography variant="body2">
                      <strong>Referral Source:</strong> {client.referral_source || 'Not specified'}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Tattoo History Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Tattoo History</Typography>
                    {isEditing && (
                      <Fab 
                        size="small" 
                        color="primary" 
                        onClick={handleTattooFileSelect}
                        aria-label="add tattoo"
                      >
                        <PhotoCameraIcon />
                      </Fab>
                    )}
                  </Box>

                  {/* Hidden file input for tattoo images */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={tattooFileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleTattooFileChange}
                  />

                  {/* Add new tattoo form */}
                  {isEditing && selectedTattooFile && (
                    <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Add New Tattoo: {selectedTattooFile.name}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description"
                              value={newTattooDescription}
                              onChange={(e) => setNewTattooDescription(e.target.value)}
                              required
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Date"
                              type="date"
                              value={newTattooDate}
                              onChange={(e) => setNewTattooDate(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Body Location"
                              value={newTattooLocation}
                              onChange={(e) => setNewTattooLocation(e.target.value)}
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleTattooUpload}
                          disabled={tattooUploadLoading || !newTattooDescription.trim()}
                        >
                          {tattooUploadLoading ? <CircularProgress size={16} /> : 'Add Tattoo'}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedTattooFile(null);
                            setNewTattooDescription('');
                            setNewTattooDate('');
                            setNewTattooLocation('');
                          }}
                        >
                          Cancel
                        </Button>
                      </CardActions>
                    </Card>
                  )}

                  {/* Display tattoo history */}
                  {tattooHistoryLoading ? (
                    <CircularProgress />
                  ) : tattooHistory.length > 0 ? (
                    <ImageList cols={2} gap={8}>
                      {tattooHistory.map((tattoo) => (
                        <TattooImageItem
                          key={tattoo.id}
                          tattoo={tattoo}
                          isEditing={isEditing}
                          onDelete={() => {
                            setTattooToDelete(tattoo);
                            setTattooDeleteDialogOpen(true);
                          }}
                          onEdit={handleEditTattoo}
                          api={api}
                          clientId={id}
                        />
                      ))}
                    </ImageList>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No tattoo history recorded
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Advanced Client Profile
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Upgrade to Standard or Growth Studio to access advanced client profile features including tattoo history, style preferences, and detailed health information.
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Client Inquiries Section - Full Width, Available to ALL Tiers */}
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 2,
                  cursor: 'pointer'
                }}
                onClick={() => setInquiriesExpanded(!inquiriesExpanded)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="action" />
                  <Typography variant="h6">
                    Inquiry History ({clientInquiries.length})
                  </Typography>
                </Box>
                <IconButton size="small">
                  <ExpandMoreIcon 
                    sx={{ 
                      transform: inquiriesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }} 
                  />
                </IconButton>
              </Box>

              <Collapse in={inquiriesExpanded}>
                {inquiriesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : clientInquiries.length > 0 ? (
                  <Grid container spacing={2}>
                    {clientInquiries.map((inquiry, index) => (
                      <Grid item xs={12} md={6} key={inquiry.id}>
                        <Card 
                          variant="outlined"
                          sx={{ 
                            height: '100%',
                            bgcolor: index === 0 ? alpha('#1976d2', 0.03) : 'background.paper',
                            borderColor: index === 0 ? 'primary.light' : 'divider'
                          }}
                        >
                          <CardContent sx={{ pb: 1 }}>
                            {/* Header with date */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                  {new Date(inquiry.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Typography>
                                {index === 0 && (
                                  <Chip label="Most Recent" size="small" color="primary" sx={{ mt: 0.5 }} />
                                )}
                              </Box>
                              <Chip 
                                label={inquiry.status?.charAt(0).toUpperCase() + inquiry.status?.slice(1) || 'New'} 
                                size="small"
                                color={inquiry.status === 'completed' ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </Box>

                            {/* Project Description */}
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {inquiry.project_description}
                            </Typography>

                            {/* Details Grid */}
                            <Grid container spacing={1}>
                              {inquiry.placement && (
                                <Grid item xs={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PlaceIcon fontSize="small" color="action" />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Placement
                                      </Typography>
                                      <Typography variant="body2">{inquiry.placement}</Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {inquiry.size_estimate && (
                                <Grid item xs={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SizeIcon fontSize="small" color="action" />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Size
                                      </Typography>
                                      <Typography variant="body2">{inquiry.size_estimate}</Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {inquiry.budget_range && (
                                <Grid item xs={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <MoneyIcon fontSize="small" color="action" />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Budget
                                      </Typography>
                                      <Typography variant="body2">{formatBudget(inquiry.budget_range)}</Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {inquiry.preferred_timeframe && (
                                <Grid item xs={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ScheduleIcon fontSize="small" color="action" />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Timeframe
                                      </Typography>
                                      <Typography variant="body2">{formatTimeframe(inquiry.preferred_timeframe)}</Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>

                            {/* Additional Notes */}
                            {inquiry.additional_notes && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                  Additional Notes
                                </Typography>
                                <Typography variant="body2">{inquiry.additional_notes}</Typography>
                              </Box>
                            )}

                            {/* Reference Images */}
                            {inquiry.reference_images && inquiry.reference_images.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <GalleryIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    Reference Images ({inquiry.reference_images.length})
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {inquiry.reference_images.map((image, imgIndex) => (
                                    <Box
                                      key={image.id || imgIndex}
                                      sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                          opacity: 0.8,
                                          borderColor: 'primary.main'
                                        }
                                      }}
                                      onClick={() => handleOpenLightbox(inquiry.reference_images, imgIndex)}
                                    >
                                      <img
                                        src={image.url}
                                        alt={`Reference ${imgIndex + 1}`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Custom Form Data */}
                            {inquiry.custom_form_data && Object.keys(inquiry.custom_form_data).length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                  Form Responses
                                </Typography>
                                <Grid container spacing={1}>
                                  {Object.entries(inquiry.custom_form_data).map(([key, value]) => (
                                    <Grid item xs={6} key={key}>
                                      <Typography variant="caption" color="text.secondary">
                                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                      </Typography>
                                      <Typography variant="body2">
                                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                                      </Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No inquiries linked to this client
                  </Typography>
                )}
              </Collapse>
            </Box>
          </Grid>

          {/* Client ID Image Section - Full Width */}
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="h6" gutterBottom>Client ID Document</Typography>
              
              {/* Hidden file input */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {/* Only show these buttons if no image exists or when selecting a new one */}
                {(!client.client_id_image_url || selectedFile) && (
                  <>
                    <Button 
                      variant="outlined" 
                      onClick={handleFileSelect}
                    >
                      Select File
                    </Button>
                    
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleFileUpload}
                      disabled={!selectedFile || uploadLoading}
                    >
                      {uploadLoading ? <CircularProgress size={24} /> : 'Upload ID'}
                    </Button>
                    
                    {selectedFile && (
                      <Typography variant="body2">
                        Selected: {selectedFile.name}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              
              {/* Display the current ID image */}
              {client.client_id_image_url && (
                <Box sx={{ mt: 2 }}>
                  {imageLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Card sx={{ maxWidth: 400 }}>
                      <CardMedia
                        component="img"
                        sx={{ height: 200, objectFit: 'contain' }}
                        image={secureImageSrc || client.client_id_image_url}
                        alt="Client ID"
                        onError={(e) => {
                          console.error('Image failed to load:', e);
                          setImageError('Failed to load image');
                        }}
                      />
                      <CardActions>
                        <Button 
                          size="small" 
                          color="primary" 
                          onClick={handleReplaceImage}
                          startIcon={<CachedIcon />}
                        >
                          Replace
                        </Button>
                        <Button 
                          size="small" 
                          color="error" 
                          onClick={openDeleteImageDialog}
                          startIcon={<DeleteIcon />}
                        >
                          Remove
                        </Button>
                      </CardActions>
                    </Card>
                  )}
                  
                  {imageError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {imageError}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Box>
            {isEditing ? (
              <>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSave}
                  sx={{ mr: 2 }}
                >
                  Save Changes
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    setIsEditing(false);
                    fetchClient(); // Reset to original data
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => setIsEditing(true)}
              >
                Edit Client
              </Button>
            )}
          </Box>
          
          <Box>
            <Button 
              variant="outlined" 
              onClick={handleViewConsentForms}
              sx={{ mr: 2 }}
            >
              View Consent Forms
            </Button>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={openDeleteDialog}
            >
              Delete Client
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Delete Client Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Client
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete this client? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Image Dialog */}
      <Dialog
        open={deleteImageDialogOpen}
        onClose={closeDeleteImageDialog}
        aria-labelledby="delete-image-dialog-title"
      >
        <DialogTitle id="delete-image-dialog-title">
          Remove Client ID Image
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the client ID image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteImageDialog}>Cancel</Button>
          <Button onClick={handleRemoveImage} color="error">
            Remove Image
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tattoo Dialog */}
      <Dialog
        open={editTattooDialogOpen}
        onClose={() => setEditTattooDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="edit-tattoo-dialog-title"
      >
        <DialogTitle id="edit-tattoo-dialog-title">
          Edit Tattoo Details
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={editTattooDescription}
                onChange={(e) => setEditTattooDescription(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={editTattooDate}
                onChange={(e) => setEditTattooDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Body Location"
                value={editTattooLocation}
                onChange={(e) => setEditTattooLocation(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTattooDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateTattoo}
            disabled={tattooUpdateLoading || !editTattooDescription.trim()}
          >
            {tattooUpdateLoading ? <CircularProgress size={20} /> : 'Update Tattoo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tattoo Dialog */}
      <Dialog
        open={tattooDeleteDialogOpen}
        onClose={() => setTattooDeleteDialogOpen(false)}
        aria-labelledby="delete-tattoo-dialog-title"
      >
        <DialogTitle id="delete-tattoo-dialog-title">
          Delete Tattoo Image
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this tattoo image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTattooDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTattoo} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'black', color: 'white' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>
            Image {lightboxIndex + 1} of {lightboxImages.length}
          </Typography>
          <IconButton onClick={handleCloseLightbox} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 0 }}>
          {lightboxImages.length > 0 && (
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Previous button */}
              {lightboxImages.length > 1 && (
                <IconButton
                  onClick={handleLightboxPrev}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <PrevIcon />
                </IconButton>
              )}
              
              {/* Image */}
              <img
                src={lightboxImages[lightboxIndex]?.url || lightboxImages[lightboxIndex]}
                alt={`Reference ${lightboxIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
              
              {/* Next button */}
              {lightboxImages.length > 1 && (
                <IconButton
                  onClick={handleLightboxNext}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <NextIcon />
                </IconButton>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ClientProfilePage;